/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { Table, App, Empty, Tooltip } from 'antd'
import { useCallback, useMemo, useRef, useState } from 'react'
import dayjs from 'dayjs'
import colors from '@sportninja/common/constants/appColors'
import { BillingScheduleItem } from '@sportninja/common/types/Registration'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import { getLocaleFromCountry } from '@sportninja/common/currency'
import IndicatorCard from 'src/components/ts-components/IndicatorCard'
import {
  WalletOutlined,
  DollarOutlined,
  FilePdfOutlined,
} from '@ant-design/icons'
import {
  formatLongDate,
  formatShortDate,
} from '@sportninja/common/ts-utils/date-utils'
import { ROUTES } from '@sportninja/common/constants/app'
import LoadingSpinner from 'src/components/LoadingSpinner'
import req from '@sportninja/common/api/request'
import useWindowSize from 'src/hooks/useWindowSize'
import {
  BillingItem,
  RegistrationDetails,
} from '@sportninja/common/hooks/useRegistrationDetails'
import Text from 'src/components/ts-components/Text'
import {
  paragraphSmall,
  paragraphXSmall,
  weightRegular,
} from 'src/components/css'

type RegistrationDetailsBillingProps = {
  selectedRegistration: RegistrationDetails | null
  currencies?: unknown[]
}

async function fetchBillingInfo(billingId) {
  const response = await req(`/billing/schedule/${billingId}/receipt`)
  return response.data
}

const tableStyles = css`
  .ant-table {
    background: transparent;
    border-radius: 8px;
    overflow: hidden;
  }
  .ant-table-thead {
    height: 32px !important;
  }
  .ant-table-thead > tr > th {
    background-color: ${colors.SECONDARY_200} !important;
    color: ${colors.NEUTRAL_70};
    text-transform: uppercase;
    white-space: nowrap;
    ${paragraphXSmall};
  }
  .ant-table-thead > tr > th:first-of-type {
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
  }
  .ant-table-thead > tr > th:last-child {
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
  }
  .ant-table-tbody {
    background-color: transparent;
  }
  .ant-table-tbody > tr:nth-of-type(even) {
    background-color: ${colors.SECONDARY_400};
  }
  .ant-table-tbody > tr:nth-of-type(odd) {
    background-color: ${colors.SECONDARY_300};
  }
  .ant-table-tbody > tr:last-child > td {
    border-bottom: none !important;
  }
  .ant-table-tbody > tr > td {
    ${paragraphSmall}
    ${weightRegular}
    color: ${colors.WHITE}
  }
  .ant-table-cell {
    vertical-align: middle !important;
  }
  th.ant-table-cell::before {
    display: none !important;
  }
  .ant-table-thead > tr > th.ant-table-cell-fix-right {
    background-color: ${colors.SECONDARY_200} !important;
  }
  .ant-table-tbody > tr:nth-of-type(even) .ant-table-cell-fix-right {
    background-color: ${colors.SECONDARY_400} !important;
  }
  .ant-table-tbody > tr:nth-of-type(odd) .ant-table-cell-fix-right {
    background-color: ${colors.SECONDARY_300} !important;
  }
`

const tooltipContentStyles = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 250px;
`

const RegistrationDetailsBilling = ({
  selectedRegistration,
}: RegistrationDetailsBillingProps) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width < 768, [width])
  const { message } = App.useApp()
  const downloadRef = useRef(null)
  const [loadingPdfIds, setLoadingPdfIds] = useState<string[]>([])
  const isPlayerRegistration = useMemo(
    () => selectedRegistration?.registration_type !== 'Team',
    [selectedRegistration?.registration_type]
  )
  const isCancelled = selectedRegistration?.status_id === 5

  const currencyCountry = useMemo(() => {
    return selectedRegistration?.billing?.[0]?.currency_country
  }, [selectedRegistration?.billing])

  const formatMoney = useCallback(
    (amount: number) => {
      const locale = getLocaleFromCountry(currencyCountry)
      const code = currencyCountry?.currency_code || 'USD'
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: code,
      }).format(Number(amount) || 0)
    },
    [currencyCountry]
  )

  const totalOutstanding = useMemo(() => {
    if (!selectedRegistration?.billing) {
      return 0
    }

    let total = 0
    selectedRegistration.billing
      ?.filter((el) =>
        isPlayerRegistration
          ? el.user_type === 'player'
          : el.user_type === 'teamRep'
      )
      .filter((el) => !el.paid || el.refunded)
      .forEach((el) => {
        if (el.refunded && el.refund_subtotal < el.subtotal) {
          total += el.subtotal - el.refund_subtotal
        } else {
          total += el.subtotal
        }
      })

    return total
  }, [isPlayerRegistration, selectedRegistration?.billing])

  const totalPaid = useMemo(() => {
    if (!selectedRegistration?.billing) {
      return 0
    }

    let total = 0
    selectedRegistration.billing
      ?.filter((el) =>
        isPlayerRegistration
          ? el.user_type === 'player'
          : el.user_type === 'teamRep'
      )
      .filter((el) => el.paid)
      .forEach((el) => {
        total += el.display_subtotal
      })

    return total
  }, [isPlayerRegistration, selectedRegistration?.billing])

  const totalAll = useMemo(() => {
    if (!selectedRegistration?.billing) {
      return 0
    }

    return selectedRegistration.billing
      ?.filter((el) =>
        isPlayerRegistration
          ? el.user_type === 'player'
          : el.user_type === 'teamRep'
      )
      .reduce((acc, el) => acc + el.subtotal, 0)
  }, [isPlayerRegistration, selectedRegistration?.billing])

  const calculatePastDues = (billingDetails: BillingScheduleItem[]) => {
    let total = 0

    billingDetails
      ?.filter((el) =>
        isPlayerRegistration
          ? el.user_type === 'player'
          : el.user_type === 'teamRep'
      )
      .forEach((el) => {
        if (el.paid) {
          return
        }

        const day = dayjs(el?.due_date, 'YYYY-MM-DD')
        const isAfter = dayjs().isAfter(day)

        if (isAfter) {
          total += el.subtotal
        }
      })

    return total
  }

  const onView = useCallback(
    async (billingId: string) => {
      setLoadingPdfIds((prev) => [...prev, billingId])
      try {
        const response = await fetchBillingInfo(billingId)
        const pdf = await req(`/billing/schedule/${billingId}/receipt/pdf`, {
          parseJSON: false,
        })
        const normalizePdf = await pdf.blob()
        const link = document.createElement('a')
        link.href = URL.createObjectURL(normalizePdf)
        link.download = `${response?.charge?.stripe_id}.pdf`
        link.click()
      } catch (err) {
        console.error(err)
        message.open({
          type: 'error',
          content: getErrorMessage(err),
        })
      } finally {
        setLoadingPdfIds((prev) => prev.filter((id) => id !== billingId))
      }
    },
    [setLoadingPdfIds, message]
  )

  const getTransactionId = useCallback((billing: BillingItem) => {
    if (!billing || billing.payment_type === 'complimentary') {
      return null
    }
    return billing?.charge?.stripe_id ?? null
  }, [])

  const renderTransactionIdRow = useCallback(
    (idValue: string | null) => (
      <div>
        <Text variant='paragraphXSmall' weight='semiBold' color={colors.WHITE}>
          ID:
        </Text>{' '}
        <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
          {idValue ?? '-'}
        </Text>
      </div>
    ),
    []
  )

  const columns = useMemo(
    () =>
      [
        {
          title: 'ID',
          dataIndex: 'invoice_id',
          key: 'invoice_id',
          width: '165px',
          ellipsis: true,
          render: (_text: string, record: BillingScheduleItem) => (
            <span>{record?.uid}</span>
          ),
        },
        {
          title: 'Type',
          dataIndex: 'payment_type',
          key: 'payment_type',
          width: '100px',
          ellipsis: true,
          render: (_text: string, record: BillingItem) => (
            <span>
              <span>
                {record.payment_type === 'complimentary'
                  ? 'Complimentary'
                  : record.single_payment
                  ? 'One Time Payment'
                  : record.payment_type === 'adhoc'
                  ? 'AdHoc'
                  : record.payment_type === 'deposit'
                  ? 'Deposit'
                  : 'Installment'}
              </span>
            </span>
          ),
        },

        {
          title: 'Status',
          key: 'status',
          align: 'center',
          width: isMobile ? 80 : 100,
          render: (record: BillingItem) => {
            const transactionId = getTransactionId(record)
            const transactionIdRow = renderTransactionIdRow(transactionId)
            const isPaid = record.paid
            const isPastDue =
              dayjs(record.due_date).isBefore(dayjs()) && !record.paid
            const isRefunded = record?.charge?.refunded
            const isFailure =
              record.status_message !== null && record.failures > 0
            const isPaidInPerson = record.paid && record.paid_in_person

            if (isRefunded) {
              const isPartialRefund =
                record?.charge?.refund_amount &&
                record?.subtotal &&
                parseFloat(record?.charge?.refund_amount.toString()) <
                  record?.subtotal

              return (
                <Tooltip
                  title={
                    <div css={tooltipContentStyles}>
                      <div>
                        <Text
                          variant='paragraphXSmall'
                          weight='semiBold'
                          color={colors.WHITE}
                        >
                          Reason:
                        </Text>{' '}
                        <Text
                          variant='paragraphSmall'
                          weight='regular'
                          color={colors.WHITE}
                        >
                          {record?.charge?.refund_reason ||
                            'No reason provided'}
                        </Text>
                      </div>
                      {record?.charge?.refund_note && (
                        <div>
                          <Text
                            variant='paragraphXSmall'
                            weight='semiBold'
                            color={colors.WHITE}
                          >
                            Note:
                          </Text>{' '}
                          <Text
                            variant='paragraphSmall'
                            weight='regular'
                            color={colors.WHITE}
                          >
                            {record.charge.refund_note}
                          </Text>
                        </div>
                      )}
                      {record?.charge?.refund_amount && (
                        <div>
                          <Text
                            variant='paragraphXSmall'
                            weight='semiBold'
                            color={colors.WHITE}
                          >
                            Refund Amount:
                          </Text>{' '}
                          <Text
                            variant='paragraphSmall'
                            weight='regular'
                            color={colors.WHITE}
                          >
                            {formatMoney(record.charge.refund_amount)}
                          </Text>
                        </div>
                      )}
                      {transactionId ? transactionIdRow : null}
                    </div>
                  }
                  placement='top'
                  zIndex={99999}
                  trigger={['hover']}
                  mouseEnterDelay={0.5}
                >
                  <span
                    css={css`
                      padding: 2px 4px;
                      border-radius: 4px;
                      border: 1px solid ${colors.INFO_500};
                      background-color: ${'rgba(6, 22, 51, 0.50)'};
                      color: ${colors.INFO_200};
                      font-size: ${isMobile ? '12px' : '14px'};
                      white-space: nowrap;
                      display: inline-block;
                      text-align: center;
                      min-width: ${isMobile ? '70px' : '70px'};
                      cursor: help;
                    `}
                  >
                    {isPartialRefund ? 'Partial Refund' : 'Refunded'}
                  </span>
                </Tooltip>
              )
            }

            if (isFailure) {
              return (
                <Tooltip
                  title={
                    <div css={tooltipContentStyles}>
                      <div>
                        <Text
                          variant='paragraphSmall'
                          weight='regular'
                          color={colors.WHITE}
                        >
                          {record.status_message}
                        </Text>
                      </div>
                      <div>
                        <Text
                          variant='paragraphSmall'
                          weight='regular'
                          color={colors.WHITE}
                        >
                          ({record.failures} Attempt
                          {record.failures > 1 ? 's' : ''})
                        </Text>
                      </div>
                      {transactionIdRow}
                    </div>
                  }
                  placement='top'
                  zIndex={99999}
                  trigger={['hover']}
                  mouseEnterDelay={0.5}
                >
                  <span
                    css={css`
                      padding: 2px 4px;
                      border-radius: 4px;
                      border: 1px solid ${colors.ERROR_500};
                      color: ${colors.ERROR_200};
                      background-color: rgba(51, 6, 6, 0.5);
                      font-size: ${isMobile ? '12px' : '14px'};
                      white-space: nowrap;
                      display: inline-block;
                      text-align: center;
                      min-width: ${isMobile ? '70px' : '70px'};
                      cursor: help;
                    `}
                  >
                    Failed
                  </span>
                </Tooltip>
              )
            }

            if (isPaidInPerson) {
              return (
                <Tooltip
                  title={
                    <div css={tooltipContentStyles}>
                      <Text
                        variant='paragraphSmall'
                        weight='regular'
                        color={colors.WHITE}
                      >
                        This payment was made in person. Receipts are not
                        available for in-person payments.
                      </Text>
                      {transactionIdRow}
                    </div>
                  }
                  placement='top'
                  zIndex={99999}
                  trigger={['hover']}
                  mouseEnterDelay={0.5}
                >
                  <span
                    css={css`
                      padding: 2px 4px;
                      border-radius: 4px;
                      border: 1px solid ${colors.INFO_500};
                      background-color: ${'rgba(6, 22, 51, 0.50)'};
                      color: ${colors.INFO_200};
                      font-size: ${isMobile ? '12px' : '14px'};
                      white-space: nowrap;
                      display: inline-block;
                      text-align: center;
                      min-width: ${isMobile ? '70px' : '70px'};
                      cursor: help;
                    `}
                  >
                    Paid (EXT)
                  </span>
                </Tooltip>
              )
            }

            const statusChip = (
              <span
                css={css`
                  padding: 2px 4px;
                  border-radius: 4px;
                  border: 1px solid
                    ${isPaid
                      ? colors.SUCCESS_500
                      : isPastDue
                      ? colors.ERROR_500
                      : colors.WARNING_500};
                  color: ${isPaid
                    ? colors.SUCCESS_200
                    : isPastDue
                    ? colors.ERROR_200
                    : colors.WARNING_200};
                  background-color: ${isPaid
                    ? 'rgba(5, 58, 17, 0.50)'
                    : isPastDue
                    ? 'rgba(51, 6, 6, 0.50)'
                    : 'rgba(51, 32, 6, 0.50)'};
                  font-size: ${isMobile ? '12px' : '14px'};
                  white-space: nowrap;
                  display: inline-block;
                  text-align: center;
                  min-width: ${isMobile ? '70px' : '70px'};
                `}
              >
                {isPaid ? 'Paid' : isPastDue ? 'Past Due' : 'Pending'}
              </span>
            )

            return (
              <Tooltip
                title={<div css={tooltipContentStyles}>{transactionIdRow}</div>}
                placement='top'
                zIndex={99999}
                trigger={['hover']}
                mouseEnterDelay={0.5}
              >
                {statusChip}
              </Tooltip>
            )
          },
        },
        {
          title: 'Due Date',
          dataIndex: 'due_date',
          key: 'due_date',
          width: '120px',
          align: 'left',
          sorter: (a, b) => dayjs(a.due_date).unix() - dayjs(b.due_date).unix(),
          render: (date: string, record: BillingItem) => (
            <span
              css={css`
                color: ${dayjs(date).isBefore(dayjs()) && !record.paid
                  ? colors.ERROR_200
                  : colors.WHITE};
              `}
            >
              {formatShortDate(date)}
            </span>
          ),
        },
        {
          title: 'Paid At',
          dataIndex: 'paid_at',
          key: 'paid_at',
          width: '120px',
          align: 'left',
          sorter: (a, b) =>
            dayjs(a.paid_at || 0).unix() - dayjs(b.paid_at || 0).unix(),
          render: (date: string | null) => (
            <span>{date ? formatShortDate(date) : '-'}</span>
          ),
        },
        {
          title: 'Subtotal',
          dataIndex: 'subtotal',
          key: 'subtotal',
          align: 'right',
          width: '150px',
          sorter: (a, b) => a.subtotal - b.subtotal,
          render: (amount: number, record: BillingItem) => (
            <span>
              {record.payment_type === 'complimentary'
                ? formatMoney(0)
                : formatMoney(amount)}
            </span>
          ),
        },
        {
          title: 'Tax',
          dataIndex: 'taxtotal',
          key: 'taxtotal',
          align: 'right',
          width: '150px',
          sorter: (a, b) => a.taxtotal - b.taxtotal,
          render: (amount: number, record: BillingItem) => (
            <span>
              {record.payment_type === 'complimentary'
                ? formatMoney(0)
                : formatMoney(amount)}
            </span>
          ),
        },
        {
          title: 'Total',
          dataIndex: 'total',
          key: 'total',
          align: 'right',
          width: '150px',
          sorter: (a, b) => a.total - b.total,
          render: (amount: number, record: BillingItem) => (
            <span>
              {record.payment_type === 'complimentary'
                ? formatMoney(0)
                : formatMoney(amount)}
            </span>
          ),
        },
        {
          title: 'Description',
          dataIndex: 'description',
          key: 'description',
          width: '300px',
          render: (_text: string, record: BillingItem) => {
            const fullText =
              record.payment_type === 'complimentary'
                ? 'Complimentary Registration'
                : record?.description || record?.charge?.description || ''
            return (
              <Tooltip title={fullText} placement='top' zIndex={12500}>
                <span
                  css={css`
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                    text-overflow: ellipsis;
                  `}
                >
                  {fullText}
                </span>
              </Tooltip>
            )
          },
        },
        {
          title: 'Action',
          key: 'action',
          align: 'center',
          width: isMobile ? 80 : 120,
          fixed: 'right' as const,
          render: (record: BillingItem) => (
            <div
              css={css`
                display: flex;
                justify-content: center;
                align-items: center;
                gap: 8px;
                width: 100%;
              `}
            >
              {!record.paid && !isCancelled && (
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    cursor: pointer;
                    border: 1px solid ${colors.PRIMARY};
                    padding: 4px 8px;
                    border-radius: 8px;
                    background-color: ${colors.PRIMARY};
                  `}
                  onClick={() => {
                    window.open(
                      `${ROUTES.REGISTRATION_PAYMENT}?teamId=${selectedRegistration?.team?.uid}&rosterId=${selectedRegistration?.team?.team_player_roster_id}&billingId=${record.uid}`,
                      '_blank'
                    )
                  }}
                >
                  <DollarOutlined
                    css={css`
                      font-size: ${isMobile ? '16px' : '20px'};
                      color: ${colors.SECONDARY_300};
                    `}
                  />
                  {!isMobile && (
                    <span
                      css={css`
                        color: ${colors.SECONDARY_300};
                        font-size: 14px;
                      `}
                    >
                      Pay
                    </span>
                  )}
                </div>
              )}
              {record.paid && !record.paid_in_person && (
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    cursor: pointer;
                    border: 1px solid ${colors.PRIMARY};
                    padding: 8px;
                    border-radius: 8px;
                    background-color: transparent;
                    min-width: ${isMobile ? '40px' : '100px'};
                    /* Remove this from now until we have a better receipt pdf */
                    display: none;
                  `}
                  ref={downloadRef}
                  onClick={() => onView(record?.uid)}
                >
                  {loadingPdfIds.includes(record.uid) ? (
                    <LoadingSpinner size={1.5} />
                  ) : (
                    <>
                      <FilePdfOutlined
                        css={css`
                          font-size: ${isMobile ? '16px' : '20px'};
                          color: ${colors.PRIMARY};
                        `}
                      />
                      {!isMobile && (
                        <span
                          css={css`
                            color: ${colors.PRIMARY};
                            font-size: 14px;
                          `}
                        >
                          Receipt
                        </span>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          ),
        },
      ].filter(Boolean),
    [
      isMobile,
      formatMoney,
      selectedRegistration?.billing?.length,
      selectedRegistration?.team?.uid,
      selectedRegistration?.team?.team_player_roster_id,
      isCancelled,
      loadingPdfIds,
      onView,
      getTransactionId,
      renderTransactionIdRow,
    ]
  )

  if (selectedRegistration?.billing?.length === 0) {
    return (
      <div
        css={css`
          display: flex;
          flex: 1;
          justify-content: center;
          align-items: center;
          height: 500px;
        `}
      >
        <Empty description='No Billing Information Available' />
      </div>
    )
  }

  return (
    <div
      css={css`
        padding: ${isMobile ? '8px' : '0'};
        display: flex;
        flex-direction: column;
        height: 100%;
        overflow-y: auto;
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: ${isMobile ? 'column' : 'row'};
          gap: 16px;
          margin-bottom: 16px;
          width: 100%;
          overflow: visible;
        `}
      >
        <IndicatorCard
          icon={<WalletOutlined />}
          title='Total Paid'
          value={`${formatMoney(totalPaid)} / ${formatMoney(totalAll)}`}
          subtitle={`Total Outstanding: ${formatMoney(totalOutstanding)}`}
          hideSmallIndicator={true}
          dynamicHeight={true}
        />

        {!!selectedRegistration?.billing?.find((el) => !el.paid)?.subtotal && (
          <IndicatorCard
            icon={<WalletOutlined />}
            title='Next Payment'
            value={formatMoney(
              selectedRegistration?.billing?.find((el) => !el.paid)?.subtotal ||
                0
            )}
            subtitle={formatLongDate(
              selectedRegistration?.billing?.find((el) => !el.paid)?.due_date ||
                ''
            )}
            hideSmallIndicator={true}
            dynamicHeight={true}
          />
        )}

        <IndicatorCard
          icon={<WalletOutlined />}
          title='Total Past Due'
          value={formatMoney(
            calculatePastDues(
              (selectedRegistration?.billing as unknown as BillingScheduleItem[]) ||
                []
            )
          )}
          subtitle=''
          hideSmallIndicator={true}
          dynamicHeight={true}
        />
      </div>

      <Table
        dataSource={selectedRegistration?.billing}
        // @ts-ignore
        columns={columns}
        pagination={false}
        rowKey={(record) => record.uid}
        scroll={{ x: isMobile ? 500 : 'max-content' }}
        size='small'
        css={tableStyles}
      />
    </div>
  )
}

export default RegistrationDetailsBilling
